(() => {
  const doc = document.documentElement;
  const loaderKey = 'alanKochLoaderSeen';
  let shouldShowLoader = true;

  try {
    shouldShowLoader = window.sessionStorage.getItem(loaderKey) !== 'true';
  } catch {
    shouldShowLoader = true;
  }

  doc.classList.add(shouldShowLoader ? 'is-ak-loading' : 'is-ak-ready');

  const lockScroll = () => {
    document.documentElement.classList.add('is-scroll-locked');
    document.body.classList.add('is-scroll-locked');
  };

  const unlockScroll = () => {
    document.documentElement.classList.remove('is-scroll-locked');
    document.body.classList.remove('is-scroll-locked');
  };

  const initMenu = () => {
    const drawer = document.querySelector('[data-ak-menu]');
    const toggle = document.querySelector('[data-ak-menu-toggle]');
    if (!drawer || !toggle) return;

    const open = () => {
      drawer.hidden = false;
      drawer.setAttribute('aria-hidden', 'false');
      drawer.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      lockScroll();
    };

    const close = () => {
      drawer.setAttribute('aria-hidden', 'true');
      drawer.classList.remove('is-open');
      drawer.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      unlockScroll();
    };

    toggle.addEventListener('click', () => {
      drawer.classList.contains('is-open') ? close() : open();
    });

    drawer.querySelectorAll('[data-ak-menu-close]').forEach((button) => {
      button.addEventListener('click', close);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });
  };

  const initHeaderSearch = () => {
    document.querySelectorAll('[data-ak-header-search]').forEach((form) => {
      const toggle = form.querySelector('[data-ak-search-toggle]');
      const input = form.querySelector('[data-ak-search-input]');
      if (!toggle || !input) return;

      const open = () => {
        form.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
        window.requestAnimationFrame(() => input.focus());
      };

      const close = () => {
        form.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
        input.blur();
      };

      toggle.addEventListener('click', () => {
        form.classList.contains('is-open') ? close() : open();
      });

      form.addEventListener('submit', (event) => {
        if (form.classList.contains('is-open') && input.value.trim()) return;
        event.preventDefault();
        open();
      });

      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
      });

      document.addEventListener('click', (event) => {
        if (!form.classList.contains('is-open') || form.contains(event.target)) return;
        close();
      });
    });
  };

  const initReveals = () => {
    const items = document.querySelectorAll('.ak-reveal, .ak-card, .ak-story, .ak-lineup, .ak-shop-lead, .ak-manifesto, .ak-newsletter, .ak-footer__panel, .ak-simple-page__card, .ak-simple-page__statement, .ak-simple-page__contact, [data-ak-page-reveal], [data-ak-product-reveal]');
    if (!items.length) return;

    if (!('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.12 }
    );

    items.forEach((item) => observer.observe(item));
  };

  const initLazyMedia = () => {
    document.querySelectorAll('img, video').forEach((media) => {
      const markLoaded = () => media.classList.add('is-ak-media-loaded');
      if (media.tagName === 'VIDEO' || media.complete || media.readyState >= 2) {
        markLoaded();
        return;
      }
      media.addEventListener('load', markLoaded, { once: true });
      media.addEventListener('loadeddata', markLoaded, { once: true });
    });
  };

  const initPageMotion = () => {
    const finish = () => {
      doc.classList.remove('is-ak-loading');
      doc.classList.add('is-ak-ready');
      try {
        window.sessionStorage.setItem(loaderKey, 'true');
      } catch {
        // Ignore storage restrictions; the loader still exits normally.
      }
    };

    if (shouldShowLoader) {
      if (document.readyState === 'complete') finish();
      else window.addEventListener('load', finish, { once: true });
    } else {
      finish();
    }

    let ticking = false;
    const syncHeader = () => {
      doc.classList.toggle('is-ak-scrolled', window.scrollY > 12);
      ticking = false;
    };

    const requestSync = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(syncHeader);
    };

    syncHeader();
    window.addEventListener('scroll', requestSync, { passive: true });
  };

  const initMagnetic = () => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const targets = document.querySelectorAll('.ak-button, .ak-card__quick, .ak-product__add, .ak-drop-slide__card a, .ak-header__nav a, .ak-header__actions a, .ak-header__search-toggle');

    targets.forEach((target) => {
      target.addEventListener('pointermove', (event) => {
        const rect = target.getBoundingClientRect();
        const x = (event.clientX - rect.left - rect.width / 2) / rect.width;
        const y = (event.clientY - rect.top - rect.height / 2) / rect.height;
        target.style.setProperty('--ak-magnetic-x', `${(x * 10).toFixed(2)}px`);
        target.style.setProperty('--ak-magnetic-y', `${(y * 6).toFixed(2)}px`);
      });

      target.addEventListener('pointerleave', () => {
        target.style.removeProperty('--ak-magnetic-x');
        target.style.removeProperty('--ak-magnetic-y');
      });
    });
  };

  const initCursor = () => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const cursor = document.createElement('span');
    cursor.className = 'ak-cursor';
    cursor.setAttribute('aria-hidden', 'true');
    document.body.append(cursor);

    let x = window.innerWidth / 2;
    let y = window.innerHeight / 2;
    let targetX = x;
    let targetY = y;

    const render = () => {
      x += (targetX - x) * 0.18;
      y += (targetY - y) * 0.18;
      cursor.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%)`;
      window.requestAnimationFrame(render);
    };

    window.addEventListener('pointermove', (event) => {
      targetX = event.clientX;
      targetY = event.clientY;
      cursor.classList.add('is-active');
    }, { passive: true });

    document.querySelectorAll('a, button, input, summary, .ak-card__media').forEach((target) => {
      target.addEventListener('pointerenter', () => cursor.classList.add('is-hovering'));
      target.addEventListener('pointerleave', () => cursor.classList.remove('is-hovering'));
    });

    render();
  };
  const initCartDrawer = () => {
    const drawer = document.querySelector('[data-cart-drawer]');
    const openButtons = document.querySelectorAll('[data-cart-open]');
    const closeButtons = drawer?.querySelectorAll('[data-cart-close]') || [];
    const itemsContainer = drawer?.querySelector('[data-cart-items]');
    const footer = drawer?.querySelector('[data-cart-footer]');
    const subtotal = drawer?.querySelector('[data-cart-subtotal]');
    const template = document.querySelector('[data-cart-item-template]');
    if (!drawer || !itemsContainer || !template) return;

    const moneyFormat = window.Shopify?.currency?.active === 'INR' ? 'Rs. ' : '';
    const formatMoney = (amount) => `${moneyFormat}${(amount / 100).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;

    const updateHeaderCount = (count) => {
      openButtons.forEach((button) => {
        const label = button.textContent.replace(/\s*\(\d+\)\s*$/, '').trim() || 'CART';
        button.textContent = count > 0 ? `${label} (${count})` : label;
      });
    };

    const open = () => {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      lockScroll();
    };

    const close = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      unlockScroll();
    };

    const renderCart = (cart) => {
      itemsContainer.innerHTML = '';
      updateHeaderCount(cart.item_count || 0);

      if (!cart.items?.length) {
        const empty = document.createElement('p');
        empty.className = 'cart-drawer__empty';
        empty.textContent = 'Your cart is empty.';
        itemsContainer.append(empty);
        if (footer) footer.hidden = true;
        return;
      }

      cart.items.forEach((item) => {
        const node = template.content.firstElementChild.cloneNode(true);
        const urlNodes = node.querySelectorAll('[data-cart-item-url]');
        const image = node.querySelector('[data-cart-item-image]');
        const title = node.querySelector('[data-cart-item-title]');
        const variant = node.querySelector('[data-cart-item-variant]');
        const quantity = node.querySelector('[data-cart-item-quantity]');
        const price = node.querySelector('[data-cart-item-price]');
        const decrease = node.querySelector('[data-cart-decrease]');
        const increase = node.querySelector('[data-cart-increase]');
        const remove = node.querySelector('[data-cart-remove]');

        urlNodes.forEach((link) => {
          link.href = item.url;
        });
        if (image && item.image) {
          image.src = `${item.image}${item.image.includes('?') ? '&' : '?'}width=192`;
          image.alt = item.product_title;
        }
        if (title) {
          title.href = item.url;
          title.textContent = item.product_title;
        }
        if (variant) variant.textContent = item.variant_title || '';
        if (quantity) quantity.textContent = item.quantity;
        if (price) price.textContent = formatMoney(item.final_line_price);

        const changeQuantity = async (nextQuantity) => {
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: Math.max(0, nextQuantity) })
          });
          renderCart(await response.json());
        };

        decrease?.addEventListener('click', () => changeQuantity(item.quantity - 1));
        increase?.addEventListener('click', () => changeQuantity(item.quantity + 1));
        remove?.addEventListener('click', () => changeQuantity(0));

        itemsContainer.append(node);
      });

      if (subtotal) subtotal.textContent = formatMoney(cart.total_price || 0);
      if (footer) footer.hidden = false;
    };

    const refreshCart = async () => {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      renderCart(cart);
      return cart;
    };

    openButtons.forEach((button) => {
      button.addEventListener('click', async (event) => {
        event.preventDefault();
        await refreshCart();
        open();
      });
    });

    closeButtons.forEach((button) => button.addEventListener('click', close));

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const submit = form.querySelector('button[type="submit"]');
        submit?.setAttribute('aria-busy', 'true');
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form)
          });
          if (!response.ok) throw new Error('Unable to add item to cart.');
          await refreshCart();
          open();
        } catch {
          form.submit();
        } finally {
          submit?.removeAttribute('aria-busy');
        }
      });
    });

    refreshCart();
  };

  const initProductMedia = () => {
    document.querySelectorAll('[data-ak-product-media]').forEach((media) => {
      const mainImage = media.querySelector('.ak-product__main-image');
      const thumbs = Array.from(media.querySelectorAll('.ak-product__thumbs a'));
      const previous = media.querySelector('[data-ak-product-prev]');
      const next = media.querySelector('[data-ak-product-next]');
      const zoom = media.querySelector('[data-ak-product-zoom]');
      const lightbox = media.querySelector('[data-ak-product-lightbox]');
      const lightboxImage = lightbox?.querySelector('img');
      const closeLightbox = lightbox?.querySelector('[data-ak-product-close]');
      const lightboxPrevious = lightbox?.querySelector('[data-ak-product-lightbox-prev]');
      const lightboxNext = lightbox?.querySelector('[data-ak-product-lightbox-next]');
      if (!mainImage) return;

      let activeIndex = Math.max(0, thumbs.findIndex((thumb) => {
        const current = mainImage.currentSrc || mainImage.src || '';
        return current && thumb.href && current.split('?')[0] === thumb.href.split('?')[0];
      }));
      const imageCache = new Map();

      const preloadImage = (src) => {
        if (!src) return null;
        if (imageCache.has(src)) return imageCache.get(src);
        const image = new Image();
        image.decoding = 'async';
        image.src = src;
        imageCache.set(src, image);
        if (image.decode) image.decode().catch(() => {});
        return image;
      };

      const preloadAround = (index) => {
        if (!thumbs.length) return;
        [-1, 0, 1].forEach((offset) => {
          const item = thumbs[(index + offset + thumbs.length) % thumbs.length];
          preloadImage(item?.href);
        });
      };

      const setSwipeOffset = (deltaX = 0) => {
        const limited = Math.max(-22, Math.min(22, deltaX * 0.22));
        mainImage.style.setProperty('--ak-product-swipe-x', `${limited.toFixed(2)}px`);
        mainImage.style.setProperty('--ak-product-swipe-scale', Math.abs(limited) > 1 ? '0.992' : '1');
      };

      const syncLightbox = () => {
        if (!lightboxImage) return;
        lightboxImage.src = mainImage.currentSrc || mainImage.src || thumbs[activeIndex]?.href || '';
        lightboxImage.alt = mainImage.alt || '';
      };

      const showImage = (index) => {
        if (!thumbs.length) return;
        activeIndex = (index + thumbs.length) % thumbs.length;
        const thumb = thumbs[activeIndex];
        const thumbImage = thumb.querySelector('img');
        const cachedImage = preloadImage(thumb.href);
        mainImage.classList.add('is-ak-swapping');
        mainImage.src = cachedImage?.currentSrc || cachedImage?.src || thumb.href;
        mainImage.removeAttribute('srcset');
        if (thumbImage?.alt) mainImage.alt = thumbImage.alt;
        thumbs.forEach((item, itemIndex) => item.classList.toggle('is-active', itemIndex === activeIndex));
        syncLightbox();
        preloadAround(activeIndex);
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => mainImage.classList.remove('is-ak-swapping'));
        });
      };

      const openLightbox = () => {
        if (!lightbox || !lightboxImage) return;
        syncLightbox();
        lightbox.hidden = false;
        lightbox.setAttribute('aria-hidden', 'false');
        lightbox.classList.add('is-open');
        lockScroll();
      };

      const hideLightbox = () => {
        if (!lightbox) return;
        lightbox.classList.remove('is-open');
        lightbox.setAttribute('aria-hidden', 'true');
        lightbox.hidden = true;
        unlockScroll();
      };

      let swipeStartX = 0;
      let swipeStartY = 0;
      let swipeIntent = null;
      let swipeActive = false;

      const resetSwipe = () => {
        swipeActive = false;
        swipeIntent = null;
        setSwipeOffset(0);
      };

      const trackSwipeIntent = (clientX, clientY) => {
        if (!swipeActive) return;
        const deltaX = clientX - swipeStartX;
        const deltaY = clientY - swipeStartY;
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);
        if (Math.max(absX, absY) < 10) return;
        if (!swipeIntent) swipeIntent = absX > absY * 1.25 ? 'x' : 'y';
        if (swipeIntent === 'x') setSwipeOffset(deltaX);
      };

      const finishSwipe = (clientX, clientY) => {
        if (!swipeActive) return;
        const deltaX = clientX - swipeStartX;
        const deltaY = clientY - swipeStartY;
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);
        const isHorizontalSwipe = (swipeIntent === 'x' || absX > absY * 1.25) && absX > 44;
        resetSwipe();
        if (isHorizontalSwipe) showImage(activeIndex + (deltaX < 0 ? 1 : -1));
      };

      thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', (event) => {
          event.preventDefault();
          showImage(index);
        });
      });

      previous?.addEventListener('click', () => showImage(activeIndex - 1));
      next?.addEventListener('click', () => showImage(activeIndex + 1));
      zoom?.addEventListener('click', openLightbox);
      if (mainImage.dataset.akSwipeBound !== 'true') {
        mainImage.dataset.akSwipeBound = 'true';
        mainImage.draggable = false;
        mainImage.addEventListener('dragstart', (event) => event.preventDefault());
        mainImage.addEventListener('pointerdown', (event) => {
          if (!thumbs.length || (event.pointerType === 'mouse' && event.button !== 0)) return;
          swipeActive = true;
          swipeIntent = null;
          swipeStartX = event.clientX;
          swipeStartY = event.clientY;
          preloadAround(activeIndex + (event.clientX < window.innerWidth / 2 ? 1 : -1));
        }, { passive: true });
        mainImage.addEventListener('pointermove', (event) => {
          trackSwipeIntent(event.clientX, event.clientY);
        }, { passive: true });
        mainImage.addEventListener('pointerup', (event) => {
          finishSwipe(event.clientX, event.clientY);
        }, { passive: true });
        mainImage.addEventListener('pointercancel', resetSwipe, { passive: true });
        mainImage.addEventListener('touchstart', (event) => {
          if (!thumbs.length || !event.touches.length) return;
          swipeActive = true;
          swipeIntent = null;
          swipeStartX = event.touches[0].clientX;
          swipeStartY = event.touches[0].clientY;
          preloadAround(activeIndex + (event.touches[0].clientX < window.innerWidth / 2 ? 1 : -1));
        }, { passive: true });
        mainImage.addEventListener('touchmove', (event) => {
          if (!event.touches.length) return;
          trackSwipeIntent(event.touches[0].clientX, event.touches[0].clientY);
        }, { passive: true });
        mainImage.addEventListener('touchend', (event) => {
          const changedTouch = event.changedTouches[0];
          if (!changedTouch) return;
          finishSwipe(changedTouch.clientX, changedTouch.clientY);
        }, { passive: true });
        mainImage.addEventListener('touchcancel', resetSwipe, { passive: true });
      }
      mainImage.addEventListener('dblclick', openLightbox);
      closeLightbox?.addEventListener('click', hideLightbox);
      lightboxPrevious?.addEventListener('click', () => showImage(activeIndex - 1));
      lightboxNext?.addEventListener('click', () => showImage(activeIndex + 1));

      lightbox?.addEventListener('click', (event) => {
        if (event.target === lightbox) hideLightbox();
      });

      document.addEventListener('keydown', (event) => {
        if (!lightbox?.classList.contains('is-open')) return;
        if (event.key === 'Escape') hideLightbox();
        if (event.key === 'ArrowLeft') showImage(activeIndex - 1);
        if (event.key === 'ArrowRight') showImage(activeIndex + 1);
      });

      showImage(activeIndex);
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => thumbs.forEach((thumb) => preloadImage(thumb.href)), { timeout: 1600 });
      } else {
        window.setTimeout(() => thumbs.forEach((thumb) => preloadImage(thumb.href)), 800);
      }
    });
  };

  const initProductOptions = () => {
    document.querySelectorAll('.ak-product__form').forEach((form) => {
      const variantsScript = form.querySelector('[data-ak-variants]');
      const variantIdInput = form.querySelector('[data-ak-variant-id]');
      if (!variantsScript || !variantIdInput) return;

      let variants = [];
      try {
        variants = JSON.parse(variantsScript.textContent || '[]');
      } catch {
        return;
      }

      const sync = () => {
        const selected = Array.from(form.querySelectorAll('.ak-product__option')).map((fieldset) => {
          const checked = fieldset.querySelector('input[type="radio"]:checked');
          return checked?.value || '';
        });

        const match = variants.find((variant) => selected.every((value, index) => variant.options?.[index] === value));
        if (!match) return;

        variantIdInput.value = match.id;
        const formId = form.getAttribute('id');
        const submitButtons = Array.from(form.querySelectorAll('button[type="submit"]'));
        if (formId) {
          submitButtons.push(...document.querySelectorAll(`button[type="submit"][form="${formId}"]`));
        }
        submitButtons.forEach((submit) => {
          submit.disabled = !match.available;
        });
        const stickyPrices = formId
          ? document.querySelectorAll(`[data-ak-product-sticky-price][data-product-form="${formId}"]`)
          : form.querySelectorAll('[data-ak-product-sticky-price]');
        stickyPrices.forEach((price) => {
          const currency = window.Shopify?.currency?.active || 'INR';
          try {
            price.textContent = new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(match.price / 100).replace('₹', 'Rs. ');
          } catch {
            price.textContent = String((match.price / 100).toLocaleString('en-IN'));
          }
        });
      };

      form.querySelectorAll('[data-ak-option]').forEach((input) => input.addEventListener('change', sync));
      sync();
    });
  };

  const initProductSizeSheets = () => {
    document.querySelectorAll('[data-ak-size-sheet]').forEach((sheet) => {
      if (sheet.dataset.akSizeSheetReady === 'true') return;
      sheet.dataset.akSizeSheetReady = 'true';

      const panel = sheet.querySelector('.ak-size-sheet__panel');
      const triggers = Array.from(document.querySelectorAll(`[data-ak-size-sheet-open][aria-controls="${sheet.id}"]`));
      const closers = Array.from(sheet.querySelectorAll('[data-ak-size-sheet-close]'));
      let previousFocus = null;

      const close = () => {
        sheet.classList.remove('is-open');
        sheet.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('is-scroll-locked');
        window.setTimeout(() => {
          sheet.hidden = true;
          previousFocus?.focus?.();
        }, 220);
      };

      const open = () => {
        previousFocus = document.activeElement;
        sheet.hidden = false;
        sheet.setAttribute('aria-hidden', 'false');
        document.body.classList.add('is-scroll-locked');
        window.requestAnimationFrame(() => {
          sheet.classList.add('is-open');
          panel?.focus?.({ preventScroll: true });
        });
      };

      triggers.forEach((trigger) => trigger.addEventListener('click', open));
      closers.forEach((closer) => closer.addEventListener('click', close));
      sheet.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
      });
    });
  };

  const initProductScroll = () => {
    const sections = Array.from(document.querySelectorAll('[data-ak-product-scroll]'));
    if (!sections.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let ticking = false;

    const update = () => {
      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        const viewport = window.innerHeight || 1;
        const progress = Math.max(-1, Math.min(1, (viewport * 0.5 - rect.top) / viewport));
        section.style.setProperty('--ak-product-parallax', progress.toFixed(3));
      });
      ticking = false;
    };

    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
  };

  const initDropCarousels = () => {
    document.querySelectorAll('[data-ak-drop-carousel]').forEach((carousel) => {
      const viewport = carousel.querySelector('[data-ak-drop-viewport]');
      const slides = Array.from(carousel.querySelectorAll('[data-ak-drop-slide]'));
      const dotsContainer = carousel.querySelector('[data-ak-drop-dots]');
      const prev = carousel.querySelector('[data-ak-drop-prev]');
      const next = carousel.querySelector('[data-ak-drop-next]');
      const interval = Number(carousel.dataset.interval || 10000);
      const autoplay = carousel.dataset.autoplay !== 'false';
      let activeIndex = 0;
      let timer;
      let pointerStartX = 0;
      let pointerStartY = 0;
      let pointerActive = false;
      let pointerIntent = null;
      let dragFrame = null;

      if (!viewport || slides.length < 1) return;

      const setActive = (index) => {
        activeIndex = (index + slides.length) % slides.length;
        carousel.style.setProperty('--ak-drop-active', activeIndex);
        slides.forEach((slide, slideIndex) => {
          let offset = slideIndex - activeIndex;
          if (offset > slides.length / 2) offset -= slides.length;
          if (offset < slides.length / -2) offset += slides.length;

          slide.classList.remove(
            'is-active',
            'is-prev',
            'is-next',
            'is-prev-2',
            'is-next-2',
            'is-hidden-card'
          );

          if (offset === 0) {
            slide.classList.add('is-active');
            slide.removeAttribute('aria-hidden');
          } else if (offset === -1) {
            slide.classList.add('is-prev');
            slide.removeAttribute('aria-hidden');
          } else if (offset === 1) {
            slide.classList.add('is-next');
            slide.removeAttribute('aria-hidden');
          } else if (offset === -2) {
            slide.classList.add('is-prev-2');
            slide.setAttribute('aria-hidden', 'true');
          } else if (offset === 2) {
            slide.classList.add('is-next-2');
            slide.setAttribute('aria-hidden', 'true');
          } else {
            slide.classList.add('is-hidden-card');
            slide.setAttribute('aria-hidden', 'true');
          }
        });
        carousel.querySelectorAll('[data-ak-drop-dot]').forEach((dot, dotIndex) => {
          dot.toggleAttribute('aria-current', dotIndex === activeIndex);
        });
      };

      const goTo = (index) => {
        const nextIndex = (index + slides.length) % slides.length;
        setActive(nextIndex);
      };

      const setDragOffset = (deltaX = 0) => {
        const limited = Math.max(-58, Math.min(58, deltaX * 0.24));
        if (dragFrame) window.cancelAnimationFrame(dragFrame);
        dragFrame = window.requestAnimationFrame(() => {
          carousel.style.setProperty('--ak-hot-drag-x', `${limited.toFixed(2)}px`);
          dragFrame = null;
        });
      };

      const resetDragOffset = () => {
        if (dragFrame) window.cancelAnimationFrame(dragFrame);
        dragFrame = null;
        carousel.style.setProperty('--ak-hot-drag-x', '0px');
      };

      const stop = () => {
        if (timer) window.clearInterval(timer);
      };

      const start = () => {
        stop();
        if (!autoplay || slides.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        timer = window.setInterval(() => goTo(activeIndex + 1), interval);
      };

      if (dotsContainer) {
        dotsContainer.innerHTML = '';
        slides.forEach((_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.setAttribute('data-ak-drop-dot', '');
          dot.setAttribute('aria-label', `Go to product ${index + 1}`);
          dot.addEventListener('click', () => {
            goTo(index);
            start();
          });
          dotsContainer.append(dot);
        });
      }

      prev?.addEventListener('click', () => {
        goTo(activeIndex - 1);
        start();
      });

      next?.addEventListener('click', () => {
        goTo(activeIndex + 1);
        start();
      });

      viewport.addEventListener('pointerdown', (event) => {
        pointerActive = true;
        pointerIntent = null;
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
        stop();
      }, { passive: true });

      viewport.addEventListener('pointermove', (event) => {
        if (!pointerActive) return;
        const deltaX = event.clientX - pointerStartX;
        const deltaY = event.clientY - pointerStartY;
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);
        if (Math.max(absX, absY) < 10) return;
        if (!pointerIntent) pointerIntent = absX > absY * 1.25 ? 'x' : 'y';
        if (pointerIntent === 'x') setDragOffset(deltaX);
      }, { passive: true });

      viewport.addEventListener('pointerup', (event) => {
        if (!pointerActive) return;
        pointerActive = false;
        const deltaX = event.clientX - pointerStartX;
        const deltaY = event.clientY - pointerStartY;
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);
        if ((pointerIntent === 'x' || absX > absY * 1.25) && absX > 44) {
          goTo(activeIndex + (deltaX < 0 ? 1 : -1));
        }
        resetDragOffset();
        pointerIntent = null;
        start();
      }, { passive: true });

      viewport.addEventListener('pointercancel', () => {
        pointerActive = false;
        pointerIntent = null;
        resetDragOffset();
        start();
      }, { passive: true });

      carousel.addEventListener('pointerenter', stop);
      carousel.addEventListener('pointerleave', start);
      carousel.addEventListener('focusin', stop);
      carousel.addEventListener('focusout', start);

      setActive(0);
      start();
    });
  };


  const initCollectionLoadMore = () => {
    document.querySelectorAll('[data-ak-load-more]').forEach((nav) => {
      const link = nav.querySelector('[data-ak-load-more-link]');
      const grid = document.querySelector('[data-ak-collection-grid]');
      if (!link || !grid) return;

      link.addEventListener('click', async (event) => {
        event.preventDefault();
        if (link.getAttribute('aria-busy') === 'true') return;
        link.setAttribute('aria-busy', 'true');
        const originalText = link.textContent;
        link.textContent = 'Loading';

        try {
          const response = await fetch(link.href, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          if (!response.ok) throw new Error('Unable to load more products.');
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const nextGrid = doc.querySelector('[data-ak-collection-grid]');
          const nextNav = doc.querySelector('[data-ak-load-more]');
          if (nextGrid) {
            nextGrid.querySelectorAll('.ak-card').forEach((card) => grid.append(card));
          }
          if (nextNav) {
            const nextLink = nextNav.querySelector('[data-ak-load-more-link]');
            if (nextLink) {
              link.href = nextLink.href;
              link.textContent = originalText;
              link.removeAttribute('aria-busy');
              return;
            }
          }
          nav.remove();
        } catch {
          window.location.href = link.href;
        }
      });
    });
  };

  const fixHotSellingProductLinks = () => {
    document.querySelectorAll('.ak-hot-card').forEach((card) => {
      const title = card.querySelector('h3')?.textContent?.trim().toLowerCase();
      if (title === 'linen shirt blue') {
        card.setAttribute('href', '/products/linen-shirt-blue');
      }
    });
  };

  const injectPdpMobileGalleryStyles = () => {
    if (!document.querySelector('.ak-product') || document.getElementById('ak-pdp-mobile-gallery-style')) return;
    const style = document.createElement('style');
    style.id = 'ak-pdp-mobile-gallery-style';
    style.textContent = "@media (max-width: 749px) {\n  .ak-product__layout {\n    display: grid !important;\n    gap: 10px !important;\n    padding: 10px 16px 24px !important;\n  }\n\n  .ak-product__media {\n    position: relative !important;\n    display: grid !important;\n    justify-items: center !important;\n    gap: 10px !important;\n    width: 100% !important;\n    margin-inline: 0 !important;\n    padding: 0 0 18px !important;\n    overflow: visible !important;\n    scroll-snap-type: none !important;\n    transform: none !important;\n  }\n\n  .ak-product__secondary {\n    display: none !important;\n  }\n\n  .ak-product__main-image {\n    display: block !important;\n    flex: none !important;\n    width: min(88vw, 360px) !important;\n    height: min(calc(88vw * 4 / 3), 480px) !important;\n    max-height: min(calc(88vw * 4 / 3), 480px) !important;\n    margin-inline: auto !important;\n    padding: 0 !important;\n    aspect-ratio: 3 / 4 !important;\n    object-fit: contain !important;\n    object-position: center !important;\n    transform: translate3d(var(--ak-product-swipe-x, 0px), 0, 0) scale(var(--ak-product-swipe-scale, 1)) !important;\n    transition: transform 180ms ease, opacity 140ms ease !important;\n    touch-action: pan-y pinch-zoom !important;\n    user-select: none !important;\n    -webkit-user-drag: none !important;\n    backface-visibility: hidden !important;\n    will-change: transform, opacity !important;\n  }\n\n  .ak-product__main-image.is-ak-swapping {\n    opacity: 0.78 !important;\n  }\n\n  .ak-product__thumbs {\n    position: static !important;\n    display: flex !important;\n    justify-content: center !important;\n    align-items: center !important;\n    width: 100% !important;\n    height: 6px !important;\n    gap: 7px !important;\n    overflow: visible !important;\n    pointer-events: none !important;\n  }\n\n  .ak-product__thumbs a {\n    flex: 0 0 auto !important;\n    display: block !important;\n    width: 6px !important;\n    height: 6px !important;\n    border: 0 !important;\n    border-radius: 999px !important;\n    background: rgba(17, 17, 17, 0.28) !important;\n    transition: width 180ms ease, background 180ms ease !important;\n  }\n\n  .ak-product__thumbs a.is-active {\n    width: 28px !important;\n    background: rgba(17, 17, 17, 0.92) !important;\n    outline: 0 !important;\n  }\n\n  .ak-product__thumbs img {\n    display: none !important;\n  }\n\n  .ak-product__summary {\n    opacity: 1 !important;\n    transform: none !important;\n    gap: 8px !important;\n  }\n\n  .ak-product__summary h1 {\n    font-size: 22px !important;\n    line-height: 28px !important;\n  }\n\n  .ak-product__description {\n    padding-top: 10px !important;\n  }\n}\n";
    document.head.append(style);
  };

  document.addEventListener('DOMContentLoaded', () => {
    injectPdpMobileGalleryStyles();
    initPageMotion();
    initLazyMedia();
    initMenu();
    initHeaderSearch();
    initCartDrawer();
    initReveals();
    initProductMedia();
    initProductOptions();
    initProductSizeSheets();
    initProductScroll();
    initCollectionLoadMore();
    fixHotSellingProductLinks();
    initDropCarousels();
    initMagnetic();
    initCursor();
  });
})();
